import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { Alert } from '@/components/ui/alert';
import { Users } from '@phosphor-icons/react';

const HEADER_ICON = <Users size={26} />;
import {
  fetchAdminUsers, fetchAdminSchools, removeRole,
  type AdminUserRow, type SchoolRow,
} from '../lib/admin-users';

const ROLES = ['admin', 'direction', 'teacher', 'tutor'] as const;
type RoleOption = (typeof ROLES)[number];

const ROLE_LABELS: Record<RoleOption, string> = {
  admin: 'Admin plateforme',
  direction: 'Direction',
  teacher: 'Enseignant',
  tutor: 'Parent / tuteur',
};

const ROLE_BADGE_BG: Record<string, string> = {
  admin: 'var(--naia-sky)',
  direction: 'var(--naia-sky)',
  teacher: 'var(--naia-green-bg)',
  tutor: 'var(--naia-card-alt)',
};
const ROLE_BADGE_TEXT: Record<string, string> = {
  admin: 'var(--naia-blue)',
  direction: 'var(--naia-blue)',
  teacher: 'var(--naia-green-deep)',
  tutor: 'var(--naia-muted)',
};

function RoleBadge({ role }: { role: string }) {
  const style = {
    '--badge-bg': ROLE_BADGE_BG[role] ?? 'var(--naia-card-alt)',
    '--badge-text': ROLE_BADGE_TEXT[role] ?? 'var(--naia-text-primary)',
  } as CSSProperties;
  return (
    <span
      className="bg-[var(--badge-bg)] text-[var(--badge-text)] px-2 py-[2px] rounded-[12px] text-[11px] font-bold whitespace-nowrap"
      style={style}
    >
      {ROLE_LABELS[role as RoleOption] ?? role}
    </span>
  );
}



export default function AdminUsersAnnuaire() {
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [filterRole, setFilterRole] = useState('');
  const [filterSchool, setFilterSchool] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [u, s] = await Promise.all([
        fetchAdminUsers({ role: filterRole || undefined, schoolId: filterSchool || undefined, page, perPage: 50 }),
        fetchAdminSchools(),
      ]);
      setUsers(u); setSchools(s); setLoadFailed(false);
    } catch {
      setUsers([]); setSchools([]); setLoadFailed(true);
    } finally { setBusy(false); }
  }, [filterRole, filterSchool, page]);
  useEffect(() => { void reload(); }, [filterRole, filterSchool, page, reload]);

  const onRemoveRole = async (roleId: string, roleName: string) => {
    if (!confirm(`Retirer le rôle « ${ROLE_LABELS[roleName as RoleOption] ?? roleName} » ?`)) return;
    setBusy(true);
    try {
      await removeRole(roleId);
      setNotice('Rôle retiré.');
      await reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/admin/users" backLabel="Utilisateurs & Rôles" icon={HEADER_ICON}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>Annuaire</h1>
            <span className={HEADER_BADGE_CLASS}>Admin</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Tous les utilisateurs de la plateforme, filtrés par rôle ou établissement.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[1100px] mx-auto px-4 pb-16 pt-4">

        {loadFailed && (
          <Alert variant="destructive" className="mb-4">
            L'annuaire n'a pas pu être chargé. Aucun utilisateur n'est affiché tant que les données réelles ne sont pas disponibles.
          </Alert>
        )}

        {notice && (
          <p className="mb-4 px-4 py-3 rounded-[10px] bg-naia-sky text-naia-blue text-sm font-semibold">{notice}</p>
        )}

        <div className="flex gap-2 flex-wrap mb-4 items-center">
          <NativeSelect
            className="min-w-[150px] bg-secondary"
            value={filterRole}
            onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}
          >
            <option value="">Tous les rôles</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </NativeSelect>
          <NativeSelect
            className="min-w-[180px] bg-secondary"
            value={filterSchool}
            onChange={(e) => { setFilterSchool(e.target.value); setPage(1); }}
          >
            <option value="">Tous les établissements</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </NativeSelect>
          <Button variant="outline" onClick={() => void reload()} disabled={busy}>
            Actualiser
          </Button>
        </div>

        {users === null ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : users.length === 0 ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucun utilisateur trouvé.</p>
        ) : (
          <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-[var(--naia-line)] bg-[var(--naia-card-alt,var(--naia-card))]">
                    {['Email', 'Nom', 'Rôles', 'Compte créé'].map((h) => (
                      <th key={h} className="text-left px-4 py-[10px] font-black text-[10.5px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--naia-line)]">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-[var(--naia-card-alt,rgba(0,0,0,0.02))] transition-colors">
                      <td className="px-4 py-3 font-medium text-[var(--naia-text-primary)] max-w-[200px] truncate">{u.email}</td>
                      <td className="px-4 py-3 text-[var(--naia-text-secondary)]">{u.display_name ?? <span className="opacity-30">—</span>}</td>
                      <td className="px-4 py-3 min-w-[220px]">
                        {u.roles.length === 0 ? (
                          <span className="text-[var(--naia-text-secondary)] opacity-40 text-[12px]">Aucun rôle</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {u.roles.map((ra) => (
                              <span key={ra.id} className="inline-flex items-center gap-1">
                                <RoleBadge role={ra.role} />
                                {ra.school_name && (
                                  <span className="text-[10px] text-[var(--naia-text-secondary)]">{ra.school_name}</span>
                                )}
                                <button
                                  type="button"
                                  title="Retirer ce rôle"
                                  onClick={() => void onRemoveRole(ra.id, ra.role)}
                                  disabled={busy}
                                  className="bg-transparent border-none cursor-pointer text-[var(--naia-text-secondary)] opacity-50 hover:opacity-100 p-0 px-[3px] text-[12px] leading-none min-w-[16px] min-h-[16px] transition-opacity"
                                  aria-label={`Retirer le rôle ${ra.role}`}
                                >
                                  ✕
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[var(--naia-text-secondary)]">
                        {new Date(u.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-4 items-center">
          <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || busy}>
            ← Préc.
          </Button>
          <span className="text-[12px] text-[var(--naia-text-secondary)]">Page {page}</span>
          <Button variant="outline" onClick={() => setPage((p) => p + 1)} disabled={(users?.length ?? 0) < 50 || busy}>
            Suiv. →
          </Button>
        </div>
      </main>
    </>
  );
}
